'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useAuthCtx } from '@/components/providers/auth-provider';
import { useMeCtx } from '@/components/providers/v1-me-provider';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';

export const MODULE_DEFS = [
  {
    name: 'voice-agent:calls',
    description: 'Voice agent calls page',
  },
  {
    name: 'voice-agent:calls:outcome',
    description: 'Outcome column & filter for voice agent calls',
  },
  {
    name: 'voice-agent:settings',
    description: 'Voice agent settings',
  },
  {
    name: 'voice-agent:voices',
    description: 'Voice agent voices page (Native request)',
  },
  {
    name: 'customers',
    description: 'Customer pages',
  },
  {
    name: 'customers:home',
    description: 'Home page becomes the minimal entry point for CSR with customer search and recently visited.',
  },
  {
    name: 'customers:booking:route-description',
    description: 'Route description in CSR assist availability results',
  },
  {
    name: 'customers:booking:advanced',
    description: 'Turns off availability distance restrictions (max drive/distance limits and office radius gates)',
  },
  {
    name: 'customers:create',
    description: 'Customer creation functionality',
  },
  {
    name: 'customers:service-plans:create',
    description: 'Instantiating service plans for customers from templates',
  },
  {
    name: 'settings',
    description: 'All office setting pages & routes',
  },
  {
    name: 'settings:integrations',
    description: 'Integrations settings',
  },
  {
    name: 'external-calls',
    description: 'External calls pages (leaderboard & call table)',
  },
  {
    name: 'external-calls:dashboard',
    description: 'External calls dashboard',
  },
  {
    name: 'settings:asset-management',
    description: 'Asset management settings',
  },
  {
    name: 'billing',
    description: 'Billing features',
  },
  {
    name: 'marketing',
    description: 'Marketing pages',
  },
  {
    name: 'scheduling',
    description: 'Scheduling features',
  },
] as const;
export type ModuleName = (typeof MODULE_DEFS)[number]['name'];

const SOLEA_DEFAULT_MODULES: ModuleName[] = [
  'voice-agent:calls',
  'voice-agent:calls:outcome',
  'voice-agent:voices',
  'voice-agent:settings',
  'customers',
  'customers:home',
  'settings',
  'settings:integrations',
  'external-calls',
  'external-calls:dashboard',
  'settings:asset-management',
  'billing',
  'marketing',
  'scheduling',
];

interface ContextType {
  isModuleEnabled: (module: ModuleName) => boolean;
  setModuleEnabled: (name: ModuleName, enabled: boolean) => void;
  enableAll: () => void;
  reset: () => void;
}

const Context = createContext<ContextType | null>(null);

export function useModulesCtx() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useCtx must be used within a Provider');
  }
  return ctx;
}

interface ModuleEnabledGuardProps {
  name: ModuleName;
  children: React.ReactNode;
}

export function ModuleEnabledGuard({ name, children }: ModuleEnabledGuardProps) {
  const { isModuleEnabled: isModuleEnabled } = useModulesCtx();
  if (!isModuleEnabled(name)) {
    return null;
  }
  return children;
}

export function useModuleEnabled(name: ModuleName) {
  const { isModuleEnabled } = useModulesCtx();
  return isModuleEnabled(name);
}

interface Props {
  children: React.ReactNode;
}

export function ModulesProvider({ children }: Props) {
  const { isSuperuser } = useAuthCtx();
  const defaultModules = useEmployeeOfficeEnabledModules();
  const localStorageModules = useLocalStorageModules();

  // A map of modules to their enabled state
  const modules = useMemo(() => {
    const modules = new Map<ModuleName, boolean>();
    for (const name of defaultModules) {
      modules.set(name, true);
    }
    // For superusers, local storage overrides take precedence
    if (isSuperuser) {
      for (const mod of localStorageModules.modules) {
        modules.set(mod.name, mod.enabled);
      }
    }
    return modules;
  }, [isSuperuser, defaultModules, localStorageModules.modules]);

  const isModuleEnabled = useCallback(
    (name: ModuleName) => {
      return modules.get(name) ?? false;
    },
    [modules]
  );

  const setModuleEnabled = useCallback(
    (name: ModuleName, enabled: boolean) => {
      let newFlags = [];
      if (localStorageModules.modules.find((v) => v.name === name)) {
        newFlags = localStorageModules.modules.map((v) => (v.name === name ? { name: v.name, enabled: enabled } : v));
      } else {
        newFlags = [...localStorageModules.modules, { name, enabled }];
      }
      localStorageModules.updateModules(newFlags);
    },
    [localStorageModules]
  );

  const enableAll = useCallback(() => {
    const newFlags = MODULE_DEFS.map((flag) => ({ name: flag.name, enabled: true }));
    localStorageModules.updateModules(newFlags);
  }, [localStorageModules]);

  const reset = useCallback(() => {
    localStorageModules.updateModules([]);
  }, [localStorageModules]);

  return (
    <Context.Provider value={{ isModuleEnabled: isModuleEnabled, setModuleEnabled, enableAll, reset }}>
      {children}
    </Context.Provider>
  );
}

function useEmployeeOfficeEnabledModules() {
  const { isSuperuser } = useAuthCtx();
  const { office } = useOfficeCtx();
  const { me } = useMeCtx();

  return useMemo(() => {
    const officeModules = office?.enabled_modules ?? [];
    const employeeModules = me?.enabled_modules ?? [];
    let set = new Set([...officeModules, ...employeeModules]);
    if (isSuperuser) {
      set = set.union(new Set(SOLEA_DEFAULT_MODULES));
    }
    return Array.from(set).map((module) => module as ModuleName);
  }, [isSuperuser, office, me]);
}

// ----------------------------------
// --- Local storage for modules ---
// ----------------------------------

type ModulesStored = {
  modules: {
    name: ModuleName;
    enabled: boolean;
  }[];
};

const ModulesStore = {
  key: 'modules',
  read: (): ModulesStored['modules'] => {
    const value = localStorage.getItem(ModulesStore.key);
    if (!value) {
      return [];
    }
    try {
      const v = JSON.parse(value) as ModulesStored;
      return v.modules;
    } catch {
      return [];
    }
  },
  store: (v: ModulesStored) => {
    localStorage.setItem(ModulesStore.key, JSON.stringify(v));
  },
};

function useLocalStorageModules() {
  const [modules, setModules] = useState(() => {
    if (typeof window !== undefined) {
      return ModulesStore.read();
    }
    return [];
  });

  const updateModules = useCallback((modules: ModulesStored['modules']) => {
    setModules(modules);
    ModulesStore.store({ modules });
  }, []);

  return { modules, updateModules };
}
